import type { LoggedInUser } from "@/services/blackfisk/auth";
import type { LabelPrinterMap, LabelType } from "@/stores/printer";

import { useBroadcastChannel } from "@vueuse/core";
import { watch } from "vue";

import { useAccount } from "@/composables/useAccount";
import { useIdentityStore } from "@/stores/identity";
import { usePrinterStore } from "@/stores/printer";

export interface BROADCAST_ACCOUNT_SWITCH {
  action: "ACCOUNT_SWITCH";
  payload: number;
}
export interface BROADCAST_LOG_IN {
  action: "LOG_IN";
  payload: LoggedInUser;
}
export interface BROADCAST_TOKEN_UPDATE {
  action: "TOKEN_UPDATE";
  payload: { accessToken: string; csrfToken: string; expiresIn: number };
}
export interface BROADCAST_LOG_OUT {
  action: "LOG_OUT";
}
export interface BROADCAST_PRINTER_LABEL_MAP {
  action: "PRINTER_LABEL_MAP";
  payload: LabelPrinterMap;
}
export type BROADCAST_MESSAGE =
  | BROADCAST_ACCOUNT_SWITCH
  | BROADCAST_LOG_IN
  | BROADCAST_TOKEN_UPDATE
  | BROADCAST_LOG_OUT
  | BROADCAST_PRINTER_LABEL_MAP;

// oxlint-disable-next-line typescript/explicit-function-return-type
export const useAuthorizationSync = () => {
  const IdentityStore = useIdentityStore();
  const PrinterStore = usePrinterStore();
  const { updateAccount } = useAccount();
  const {
    isSupported,
    data,
    // channel,
    post,
    // close,
    // error,
    // isClosed,
  } = useBroadcastChannel<BROADCAST_MESSAGE, BROADCAST_MESSAGE>({
    name: "bf-identity",
  });
  watch(data, () => {
    switch (data.value.action) {
      case "ACCOUNT_SWITCH":
        if (data.value.payload > 0) {
          updateAccount(data.value.payload);
        }
        break;
      case "LOG_IN":
        IdentityStore.parseLoggedInUser(data.value.payload);
        break;
      case "TOKEN_UPDATE":
        IdentityStore.noteBroadcastTokenUpdate();
        // * Adopt the sibling's token without re-broadcasting — re-broadcasting from the receive
        //   handler makes 2+ tabs ping-pong the same token forever (infinite loop). The original
        //   broadcast already reached every sibling tab, so propagation needs no re-broadcast.
        IdentityStore.setSessionTokens(
          data.value.payload.accessToken,
          data.value.payload.csrfToken,
          data.value.payload.expiresIn,
          0,
          false,
        );
        break;
      case "LOG_OUT":
        // * Only act when there's an actual session to clear — if we're already logged out
        //   (re-entrant broadcast from a dead-session 401 chain), skip so we don't call
        //   sessionLogout() again and 401-recurse into a LOG_OUT ping-pong loop.
        if (IdentityStore.isAuthenticated) {
          IdentityStore.logout({ broadcast: false });
        }
        break;
      case "PRINTER_LABEL_MAP":
        for (const [label, printerId] of Object.entries(data.value.payload)) {
          PrinterStore.updateLabelPrinterMap(label as LabelType, printerId);
        }
        break;
    }
  });
  const broadcast = (message: BROADCAST_MESSAGE): void => {
    if (isSupported) {
      post(message);
    }
  };
  return {
    data,
    broadcastAccountId: (accountId: number): void => {
      broadcast({
        action: "ACCOUNT_SWITCH",
        payload: accountId,
      });
    },
    broadcastLogin: (user: LoggedInUser): void => {
      broadcast({
        action: "LOG_IN",
        payload: user,
      });
    },
    broadcastTokenUpdate: (accessToken: string, csrfToken: string, expiresIn = 0): void => {
      broadcast({
        action: "TOKEN_UPDATE",
        payload: { accessToken, csrfToken, expiresIn },
      });
    },
    broadcastLogout: (): void => {
      broadcast({
        action: "LOG_OUT",
      });
    },
    broadcastLabelPrinterMap: (labelPrinterMap: LabelPrinterMap): void => {
      broadcast({
        action: "PRINTER_LABEL_MAP",
        payload: labelPrinterMap,
      });
    },
  };
};
